import { PlpFacetedSearchSubPage, PlpSubpage as PlpSubpage } from "./use-plp-subpage";
import { plpSubpage } from "./plp-subpage";

const KNOWN_KEYS = {
  category: "category",
  family: "family",
  subfamily: "subfamily",
  product: "product",
  "spare-parts": "spare-parts",
  openedFilterTabByFilterKey: "opened-filter-tab-by-filter-key",
};

export const PlpSitemap = {
  decode: (query: URLSearchParams): PlpSubpage => {
    const spareParts = query.get(KNOWN_KEYS["spare-parts"]) === "true";
    const categoryKey = query.get(KNOWN_KEYS.category);
    const familyKey = query.get(KNOWN_KEYS.family);
    const subfamilyKey = query.get(KNOWN_KEYS.subfamily);
    const productKey = query.get(KNOWN_KEYS.product);

    if (spareParts) {
      if (productKey && subfamilyKey && categoryKey) {
        return plpSubpage.productSpareParts({ categoryKey: categoryKey, subfamilyKey, productKey });
      } else if (subfamilyKey && categoryKey) {
        return plpSubpage.subfamily({ categoryKey: categoryKey, subfamilyKey });
      } else if (categoryKey) {
        return plpSubpage.category({ categoryKey: categoryKey });
      }
    }

    const seed: PlpFacetedSearchSubPage["activatedFacets"] = familyKey ? { [KNOWN_KEYS.family]: [familyKey] } : {};
    const activatedFacets = [...query.entries()].reduce((acc, [key, value]) => {
      if (!Object.values(KNOWN_KEYS).includes(key)) {
        acc[key] = [value];
      }
      return acc;
    }, seed);

    const openedFilterTabByFilterKey = query.get(KNOWN_KEYS.openedFilterTabByFilterKey) || undefined;

    return Object.keys(activatedFacets).length > 0
      ? plpSubpage.facetedSearch({ openedFilterTabByFilterKey, activatedFacets })
      : plpSubpage.root({ openedFilterTabByFilterKey });
  },
  encode: (subpage: PlpSubpage): URLSearchParams => {
    let ret = new URLSearchParams();

    switch (subpage.tag) {
      case "root": {
        ret = new URLSearchParams();
        typeof subpage.openedFilterTabByFilterKey !== "undefined" &&
          ret.set(KNOWN_KEYS.openedFilterTabByFilterKey, subpage.openedFilterTabByFilterKey);
        return ret;
      }
      case "faceted-search": {
        ret = new URLSearchParams();
        Object.entries(subpage.activatedFacets).forEach(([key, value]) => {
          value.length > 0 && ret.set(key, value[0]!);
        });
        typeof subpage.openedFilterTabByFilterKey !== "undefined" &&
          ret.set(KNOWN_KEYS.openedFilterTabByFilterKey, subpage.openedFilterTabByFilterKey);

        return ret;
      }
      case "category": {
        ret.set(KNOWN_KEYS.category, subpage.categoryKey);
        ret.set(KNOWN_KEYS["spare-parts"], true.toString());

        return ret;
      }
      case "subfamily": {
        ret.set(KNOWN_KEYS.category, subpage.categoryKey);
        ret.set(KNOWN_KEYS.subfamily, subpage.subfamilyKey);
        ret.set(KNOWN_KEYS["spare-parts"], true.toString());

        return ret;
      }
      case "product": {
        ret.set(KNOWN_KEYS.category, subpage.categoryKey);
        ret.set(KNOWN_KEYS.subfamily, subpage.subfamilyKey);
        ret.set(KNOWN_KEYS.product, subpage.productKey);
        ret.set(KNOWN_KEYS["spare-parts"], true.toString());

        return ret;
      }
      case "product-spare-parts": {
        ret.set(KNOWN_KEYS["spare-parts"], "true");
        ret.set(KNOWN_KEYS.category, subpage.categoryKey);
        ret.set(KNOWN_KEYS.subfamily, subpage.subfamilyKey);
        ret.set(KNOWN_KEYS.product, subpage.productKey);
        ret.set(KNOWN_KEYS["spare-parts"], true.toString());

        return ret;
      }
      default: {
        const _: never = subpage;

        return _;
      }
    }
  },
  linkTo:
    (pagePath: string): PlpSitemapLinker =>
    (key, payload) => {
      const params = PlpSitemap.encode(plpSubpage[key](payload));

      return {
        pathname: pagePath,
        search: `?${params.toString()}`,
      };
    },
};

export type InternalLinkProps = { pathname: string; search: string };
export type PlpSitemapLinker = <K extends PlpSubpageKey>(key: K, payload: Payload<K>) => InternalLinkProps;

type PlpSubpageType = typeof plpSubpage;
type PlpSubpageKey = keyof PlpSubpageType;

type Payload<X extends PlpSubpageKey> =
  Extract<PlpSubpageType, { [key in X]: any }> extends {
    [key in X]: infer U;
  }
    ? U extends (...args: Array<any>) => any
      ? Parameters<U>[0]
      : never
    : never;
